const DEV_ENV = import.meta.env.VITE_ENV;

// types
import type { UserCard, QRCodeData } from '@libs/types';

// utils
import { handleUnauthorizedError } from './utils';

// For dev
import { cardReadyResponse } from '@hooks/stub-data-dev.ts';

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface ApiRequestOptions {
  method: ApiMethod;
  endpoint: string;
  data?: FormData | null;
  headers?: Record<string, string>;
}

const BASE_URL = '/api/onboarding';

export const apiRequest = async <T>(params: ApiRequestOptions): Promise<T> => {
  const { method, endpoint, data, headers = {} } = params;

  const options: RequestInit = {
    method,
    headers,
    body: data,
  };

  const url = `${BASE_URL}/${endpoint}`;

  const response = await fetch(url, options);

  if (response.status === 401) {
    handleUnauthorizedError();
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Something went wrong');
  }

  return response.json() as Promise<T>;
};

export type CardData = {
  user_card: UserCard;
  to_lk: number | null;
};

export const getOnboardingData = async (): Promise<CardData> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => resolve(cardReadyResponse));
  }

  return apiRequest<CardData>({
    method: 'GET',
    endpoint: 'card-ready',
  });
};

export const sendCardRewardData = async (formData: FormData) => {
  return apiRequest<{ status: string }>({
    method: 'POST',
    endpoint: 'card-reward',
    data: formData,
  });
};

export const getQrCodeData = async (): Promise<QRCodeData> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) =>
      resolve({
        qr_path: 'card_images/qrcode/f6ab8621-7299-4f14-819b-435c8d5f4c8d.png',
        qr_url: 'https://turbopush.io/card/1095',
        qr_download_link: 'https://turbopush.io/account/qr_code/1095/download',
        qr_name: 15,
      }),
    );
  }

  return apiRequest<QRCodeData>({
    method: 'GET',
    endpoint: 'qr-code',
  });
};

export const getTotalCards = async (): Promise<{ total_cards: number }> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => resolve({ total_cards: 2 }));
  }

  return apiRequest<{ total_cards: number }>({
    method: 'GET',
    endpoint: 'total-cards',
  });
};

export const createCardMunualy = async (formData: FormData) => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => resolve({ total_cards: 2 }));
  }

  return apiRequest<{ status: string }>({
    method: 'POST',
    endpoint: 'card-constructor',
    data: formData,
  });
};
